import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AUDIT_ACTIONS,
  AUDIT_ACTION_LABELS,
  type AuditAction,
  type AuditLog,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDateTime, formatRelative } from '@/lib/format';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';

/**
 * Trilha de auditoria — somente leitura, por definição.
 *
 * A API não expõe rota de alteração nem de exclusão: um registro de auditoria
 * que pode ser editado não serve para auditar nada.
 */
const ACTION_TONE: Partial<Record<AuditAction, BadgeKind>> = {
  CREATE: 'ok',
  UPDATE: 'info',
  DELETE: 'bad',
  LOGIN_FAILED: 'warn',
  REVOKE: 'warn',
  PASSWORD_RESET: 'warn',
  APPROVE: 'ok',
  REJECT: 'bad',
};

export function AuditoriaPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const query = useQuery({
    queryKey: ['audit-logs', { search, action, entity, page }],
    queryFn: () =>
      api.get<Paginated<AuditLog>>('/audit-logs', {
        search: search || undefined,
        action: action || undefined,
        entity: entity || undefined,
        page,
        perPage: 25,
      }),
  });

  const columns: Array<Column<AuditLog>> = [
    {
      key: 'createdAt',
      label: 'Quando',
      mono: true,
      render: (log) => <span title={formatDateTime(log.createdAt)}>{formatRelative(log.createdAt)}</span>,
    },
    { key: 'userName', label: 'Quem', strong: true, render: (log) => log.userName ?? 'Sistema' },
    {
      key: 'action',
      label: 'O quê',
      render: (log) => (
        <StatusBadge kind={ACTION_TONE[log.action] ?? 'mute'}>
          {AUDIT_ACTION_LABELS[log.action]}
        </StatusBadge>
      ),
    },
    { key: 'entity', label: 'Entidade', render: (log) => log.entity },
    { key: 'entityLabel', label: 'Registro', render: (log) => log.entityLabel ?? '—' },
    {
      key: 'changedFields',
      label: 'Campos alterados',
      render: (log) =>
        log.changedFields.length > 0 ? (
          <span className="mono">{log.changedFields.slice(0, 3).join(', ')}{log.changedFields.length > 3 ? '…' : ''}</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'statusCode',
      label: 'HTTP',
      right: true,
      mono: true,
      render: (log) => (log.statusCode === null ? '—' : String(log.statusCode)),
    },
  ];

  const hasFilters = Boolean(search || action || entity);

  return (
    <>
      <WorkspaceHeader
        title="Trilha de auditoria"
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por registro, pessoa ou entidade…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'action',
              label: 'Ação',
              value: action,
              options: AUDIT_ACTIONS.map((value) => ({
                value,
                label: AUDIT_ACTION_LABELS[value],
              })),
              onChange: (value) => {
                setAction(value);
                setPage(1);
              },
            },
            {
              key: 'entity',
              label: 'Entidade',
              value: entity,
              options: [
                { value: 'Person', label: 'Pessoa' },
                { value: 'User', label: 'Usuário' },
                { value: 'Profile', label: 'Perfil' },
                { value: 'CostCenter', label: 'Centro de custo' },
                { value: 'ChartAccount', label: 'Conta' },
                { value: 'Category', label: 'Categoria' },
                { value: 'ApiKey', label: 'Chave de API' },
                { value: 'Session', label: 'Sessão' },
                { value: 'Unit', label: 'Unidade' },
              ],
              onChange: (value) => {
                setEntity(value);
                setPage(1);
              },
            },
          ]}
        />

        {query.isPending && <TableSkeleton columns={7} />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}
        {query.data && (
          <SmartDataTable
            caption="Trilha de auditoria"
            columns={columns}
            rows={query.data.data}
            meta={query.data.meta}
            selectedId={selected?.id}
            onRowClick={setSelected}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setAction('');
                    setEntity('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhuma ação registrada ainda"
                  description="Toda ação crítica na plataforma aparece aqui automaticamente."
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? AUDIT_ACTION_LABELS[selected.action] : ''}
        subtitle={selected ? formatDateTime(selected.createdAt) : undefined}
        tabs={['Resumo', 'Antes', 'Depois']}
      >
        {(tab) =>
          !selected ? null : tab === 0 ? (
            <>
              <ReadField label="Quem" value={selected.userName ?? 'Sistema'} />
              <ReadField label="Entidade" value={selected.entity} />
              <ReadField label="Registro" value={selected.entityLabel ?? selected.entityId ?? '—'} />
              <ReadField
                label="Campos alterados"
                value={selected.changedFields.length ? selected.changedFields.join(', ') : '—'}
                mono
              />
              {selected.reason && <ReadField label="Justificativa" value={selected.reason} />}
              <ReadField
                label="Rota"
                value={`${selected.method ?? '—'} ${selected.path ?? ''}`.trim()}
                mono
              />
              <ReadField
                label="Resposta HTTP"
                value={selected.statusCode === null ? '—' : String(selected.statusCode)}
                mono
              />
              <ReadField label="Origem (IP)" value={selected.ip ?? '—'} mono />
              <ReadField label="Código da requisição" value={selected.requestId ?? '—'} mono />
            </>
          ) : (
            <JsonBlock value={tab === 1 ? selected.before : selected.after} />
          )
        }
      </ContextPanel>
    </>
  );
}

/** Mostra o estado gravado. Segredos já vêm redigidos pela API como "[oculto]". */
function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <p className="muted" style={{ fontSize: 13 }}>Nada registrado para este momento.</p>;
  }
  return (
    <pre
      className="mono"
      style={{
        background: 'var(--surface)',
        borderRadius: 10,
        padding: 14,
        fontSize: 12,
        lineHeight: 1.6,
        overflowX: 'auto',
        margin: 0,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
